/**
 * GameStage — host for a single ClientGame inside a PixiJS Application.
 * Surfaces init/load failures visibly. Visible 3-2-1 countdown overlay,
 * platform-aware in-game control hint pill.
 */
import { useEffect, useRef, useState } from 'react';
import type { ClientGame, ClientGameContext, RoundOutcome, ServerPlayerRef } from '@tt/types';
import { audio } from '@tt/audio';
import { loadGame } from './loaders';
import { StageBackdrop } from './StageBackdrop';

interface InputBindings {
  primary: { label: string; key?: string[] };
  alt?: Array<{ label: string; key?: string[] }>;
}

interface GameMeta {
  name: string;
  pitch: string;
  category: string;
  chaoticness: number;
  durationSec: number;
  controls: string[];
  tutorial?: { headline: string; tip: string };
}

interface Props {
  matchId: string;
  gameId: string;
  startsAt: number;
  endsAt: number;
  params: unknown;
  mySlot: 0 | 1 | 2 | 3;
  players: ServerPlayerRef[];
  tickPayload: unknown;
  result: RoundOutcome | null;
  sendInput(payload: unknown): void;
  inputBindings?: InputBindings | undefined;
  gameMeta?: GameMeta | undefined;
  eventMode?: 'standard' | 'lightning' | 'comeback' | 'chaos' | 'cozy';
}

export function GameStage(props: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tickHandlersRef = useRef<Set<(p: unknown) => void>>(new Set());
  const endHandlersRef = useRef<Set<(r: RoundOutcome) => void>>(new Set());
  const startHandlersRef = useRef<Set<(p: unknown) => void>>(new Set());
  const lastTickRef = useRef<unknown>(undefined);
  const lastResultRef = useRef<RoundOutcome | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'booting' | 'countdown' | 'playing' | 'ended'>('booting');

  useEffect(() => {
    if (props.tickPayload !== undefined && props.tickPayload !== lastTickRef.current) {
      lastTickRef.current = props.tickPayload;
      for (const cb of tickHandlersRef.current) cb(props.tickPayload);
    }
  }, [props.tickPayload]);

  useEffect(() => {
    if (props.result && props.result !== lastResultRef.current) {
      lastResultRef.current = props.result;
      for (const cb of endHandlersRef.current) cb(props.result);
      setPhase('ended');
    }
  }, [props.result]);

  useEffect(() => {
    let cancelled = false;
    let app: import('pixi.js').Application | null = null;
    let game: ClientGame | null = null;
    let host: HTMLDivElement | null = null;

    // Tag the body while a game is mounted so global CSS can disable
    // user-select / iOS touch-callout on every element. Without this,
    // press-and-hold a sprint/shoot button selects on-screen text and
    // turns the screen blue.
    document.body.classList.add('in-game');

    void (async () => {
      try {
        await import('pixi.js/unsafe-eval');
        const [{ Application }, gameModule] = await Promise.all([
          import('pixi.js'),
          loadGame(props.gameId),
        ]);
        if (cancelled || !containerRef.current) return;
        if (!gameModule) {
          setBootError(`Unknown game: ${props.gameId}`);
          return;
        }

        const root = document.createElement('div');
        root.className = 'game-host';
        containerRef.current.appendChild(root);
        host = root;

        const rect = containerRef.current.getBoundingClientRect();
        const w = Math.max(280, Math.floor(rect.width || window.innerWidth - 32));
        const h = Math.max(360, Math.floor(rect.height || Math.min(560, window.innerHeight * 0.6)));

        // Mobile thermal budget: cap render to 30 Hz on touch devices
        // (was implicit 60 Hz). Halves GPU + CPU per-frame work without
        // dropping resolution — the crisp text + shapes stay; only the
        // animation cadence is slower (imperceptible for these games).
        // Earlier we tried resolution: 1 on mobile but text + shapes
        // looked blurry on retina, so we keep DPR-matched resolution.
        const isMobile = (window.innerWidth < 768) || ('ontouchstart' in window);
        app = new Application();
        await app.init({
          width: w, height: h,
          background: '#0d0a14',
          antialias: true,
          autoDensity: true,
          resolution: Math.min(2, window.devicePixelRatio ?? 1),
        });
        if (cancelled) {
          app.destroy(true, { children: true, texture: true });
          return;
        }
        if (isMobile) app.ticker.maxFPS = 30;
        // Pause the Pixi ticker when the tab is backgrounded. Browsers
        // throttle rAF to ~1Hz on hidden tabs, but the ticker still
        // wakes up and drives Pixi each tick — wasted work + battery.
        const onVisibility = () => {
          if (!app) return;
          if (document.hidden) app.ticker.stop();
          else app.ticker.start();
        };
        document.addEventListener('visibilitychange', onVisibility);
        const canvas = app.canvas;
        canvas.style.display = 'block';
        canvas.style.width = '100%';
        canvas.style.maxWidth = `${w}px`;
        canvas.style.height = 'auto';
        canvas.style.touchAction = 'none';
        canvas.style.borderRadius = '16px';
        root.appendChild(canvas);

        const onResize = () => {
          if (!app || !containerRef.current) return;
          const r = containerRef.current.getBoundingClientRect();
          const nw = Math.max(280, Math.floor(r.width || w));
          app.renderer.resize(nw, h);
          if (app.stage) app.stage.hitArea = app.screen;
          canvas.style.maxWidth = `${nw}px`;
        };
        window.addEventListener('resize', onResize);

        setPhase('countdown');
        const countdownInterval = window.setInterval(() => {
          if (Date.now() >= props.startsAt) {
            setPhase('playing');
            window.clearInterval(countdownInterval);
          }
        }, 80);

        // Audio cues for 3-2-1-GO are fired by each per-game client during
        // its own warmup phase (themed to the game). Firing them ALSO from
        // the host produced doubled beats. Keep these no-ops to preserve
        // the cleanup contract (`clearInterval(beatInterval)`).
        const beatInterval = window.setInterval(() => { /* host countdown audio disabled */ }, 80);
        const goTimer = window.setTimeout(() => {
          window.clearInterval(beatInterval);
        }, Math.max(0, props.startsAt - Date.now()));

        const ctx: ClientGameContext = {
          root, pixi: app,
          mySlot: props.mySlot,
          players: props.players,
          startsAt: props.startsAt,
          endsAt: props.endsAt,
          feedback: {
            tick: () => audio.sfx.tap(),
            success: () => audio.sfx.success(),
            fail: () => audio.sfx.fail(),
            cheer: () => audio.sfx.cheer(),
            bell: () => audio.sfx.bell(),
          },
          net: {
            sendInput: (payload) => props.sendInput(payload),
            onTick: (cb) => {
              tickHandlersRef.current.add(cb);
              if (lastTickRef.current !== undefined) cb(lastTickRef.current);
              return () => { tickHandlersRef.current.delete(cb); };
            },
            onEnd: (cb) => { endHandlersRef.current.add(cb); return () => { endHandlersRef.current.delete(cb); }; },
            onStart: (cb) => { startHandlersRef.current.add(cb); cb(props.params); return () => { startHandlersRef.current.delete(cb); }; },
          },
        };

        game = gameModule;
        await game.start(ctx);

        (host as HTMLDivElement & { __cleanup?: () => void }).__cleanup = () => {
          window.removeEventListener('resize', onResize);
          document.removeEventListener('visibilitychange', onVisibility);
          window.clearInterval(countdownInterval);
          window.clearInterval(beatInterval);
          window.clearTimeout(goTimer);
        };
      } catch (e) {
        console.error('[GameStage] boot failed', e);
        setBootError((e as Error).message ?? 'unknown error');
      }
    })();

    return () => {
      cancelled = true;
      document.body.classList.remove('in-game');
      void (async () => {
        try { if (game) await game.stop(); } catch (e) { console.error('game stop', e); }
        try {
          if (host && (host as HTMLDivElement & { __cleanup?: () => void }).__cleanup) {
            (host as HTMLDivElement & { __cleanup?: () => void }).__cleanup!();
          }
        } catch { /* */ }
        try { if (host && host.parentElement) host.parentElement.removeChild(host); } catch { /* */ }
        try { if (app) app.destroy(true, { children: true, texture: true }); } catch { /* */ }
        tickHandlersRef.current.clear();
        endHandlersRef.current.clear();
        startHandlersRef.current.clear();
        lastTickRef.current = undefined;
        lastResultRef.current = null;
      })();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.matchId, props.gameId]);

  return (
    <>
      {/* Shared tavern-wall backdrop that fills the empty side gutters
       * around the centered canvas on wide PC viewports. Fixed-positioned
       * at z-index 0 behind #root (z-index 1, isolation: isolate), pure
       * CSS + inline SVG, hidden under 1100px. Single shared design across
       * every game — the game canvas always stays the visual focus. */}
      <StageBackdrop />
      <div ref={containerRef} className="game-stage">
        {bootError && (
        <div className="game-boot-error" role="alert">
          <strong>Couldn't load game.</strong>
          <span className="muted">{bootError}</span>
          <span className="muted">This can happen after an update or interrupted mobile load.</span>
          <button type="button" className="btn primary" onClick={() => window.location.reload()}>Retry</button>
        </div>
      )}
      {/* The React-side CountdownOverlay was causing a "double countdown"
          UX — every per-game client also draws its own 3-2-1-GO during
          the server warmup phase, themed to the game (sky-sieve has stars,
          cellar-sweep has dim torches, etc.). Render the per-game countdown
          only — the host now passes silently from boot → playing.
          InputHintBadge removed 2026-05-12 — covered the canvas during play;
          UniversalMatchIntro shows the same key+touch hint pre-round. */}
      </div>
    </>
  );
}
